import { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Camera, FileText, Loader2, Video } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

export default function NoteComposer({
  appointmentId, actions, beforeCreate, onSaved, onCancel, autoFocus = false, onStartAssessment,
}) {
  const [draft, setDraft] = useState("");
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const pdfRef = useRef(null);

  const canCreate = async () => {
    if (!beforeCreate) return true;

    try {
      return await beforeCreate(appointmentId);
    } catch (error) {
      console.error("Unable to create note:", error);
      return false;
    }
  };

  const saveText = async (event) => {
    event.preventDefault();
    if (!draft.trim() || !(await canCreate())) return;

    try {
      const saved = await actions.createTextNote({ appointmentId, content: draft });
      if (saved) {
        setDraft("");
        onSaved?.(appointmentId);
      }
    } catch (error) {
      console.error("Unable to create note:", error);
    }
  };

  const addMedia = (type) => async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (await canCreate()) {
        const saved = await actions.createMediaNote({ appointmentId, file, type });
        if (saved) onSaved?.(appointmentId);
      }
    } catch (error) {
      console.error("Unable to create note:", error);
    } finally {
      event.target.value = "";
    }
  };

  return (
    <form onSubmit={saveText} className="space-y-3" aria-label="New clinical note">
      <label htmlFor={`clinical-note-${appointmentId}`} className="text-sm font-medium text-slate-700">
        Clinical note
      </label>
      <Textarea
        id={`clinical-note-${appointmentId}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === "Enter") saveText(event);
        }}
        autoFocus={autoFocus}
        rows={4}
        className="min-h-[120px] resize-y"
        data-testid="note-input"
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={addMedia("photo")} />
          <Button type="button" variant="outline" size="sm" onClick={() => photoRef.current?.click()} className="whitespace-nowrap">
            <Camera size={16} className="mr-1.5" /> Photo
          </Button>
          <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={addMedia("video")} />
          <Button type="button" variant="outline" size="sm" onClick={() => videoRef.current?.click()} className="whitespace-nowrap">
            <Video size={16} className="mr-1.5" /> Video
          </Button>
          <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={addMedia("pdf")} />
          <Button type="button" variant="outline" size="sm" onClick={() => pdfRef.current?.click()} className="whitespace-nowrap">
            <FileText size={16} className="mr-1.5" /> PDF
          </Button>
          {onStartAssessment && (
            <Button type="button" variant="outline" size="sm" onClick={async () => { if (await canCreate()) onStartAssessment(appointmentId); }} className="whitespace-nowrap">
              <FileText size={16} className="mr-1.5" /> Fill assessment
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {onCancel && <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>}
          <Button type="submit" size="sm" disabled={!draft.trim() || actions.uploadingNoteId !== null} className="whitespace-nowrap">
            Save note
          </Button>
        </div>
      </div>

      {actions.uploadingNoteId && (
        <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <Loader2 size={14} className="mr-2 inline animate-spin" />
          Uploading… {actions.uploadProgress}%
        </div>
      )}
    </form>
  );
}

NoteComposer.propTypes = {
  appointmentId: PropTypes.string.isRequired,
  actions: PropTypes.shape({
    createTextNote: PropTypes.func.isRequired,
    createMediaNote: PropTypes.func.isRequired,
    uploadingNoteId: PropTypes.string,
    uploadProgress: PropTypes.number.isRequired,
  }).isRequired,
  beforeCreate: PropTypes.func,
  onSaved: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  onStartAssessment: PropTypes.func,
};
